import React, { useState, useEffect } from "react";
import {
  Calendar,
  Clock,
  Plus,
  Trash2,
  Save,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Pause,
  Square,
  BarChart3,
  Download,
} from "lucide-react";
import Button from "../../../components/common/Button/Button";
import PermissionGate from "../../../components/common/PermissionGate/PermissionGate";

const Timesheet = ({ onBack, userRole }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [timesheetEntries, setTimesheetEntries] = useState([]);
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [activeTimer, setActiveTimer] = useState(null);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [viewMode, setViewMode] = useState("daily"); // daily, weekly, monthly
  const [selectedProject, setSelectedProject] = useState("all");
  const [formData, setFormData] = useState({
    project: "",
    task: "",
    description: "",
    startTime: "",
    endTime: "",
    hours: "",
    billable: true,
  });

  useEffect(() => {
    loadTimesheetEntries();
  }, [currentDate, viewMode, selectedProject]);

  useEffect(() => {
    let interval;
    if (activeTimer) {
      interval = setInterval(() => {
        setTimerSeconds((prev) => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [activeTimer]);

  const loadTimesheetEntries = async () => {
    try {
      // Get user data from localStorage
      const userData = JSON.parse(localStorage.getItem('user_data') || '{}');

      // Build query parameters
      const queryParams = new URLSearchParams();
      if (selectedProject !== "all") {
        queryParams.append("search", selectedProject);
      }
      if (viewMode === "daily") {
        queryParams.append(
          "startDate",
          currentDate.toISOString().split("T")[0]
        );
        queryParams.append("endDate", currentDate.toISOString().split("T")[0]);
      } else if (viewMode === "weekly") {
        const startOfWeek = new Date(currentDate);
        startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        queryParams.append(
          "startDate",
          startOfWeek.toISOString().split("T")[0]
        );
        queryParams.append("endDate", endOfWeek.toISOString().split("T")[0]);
      } else if (viewMode === "monthly") {
        const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
        queryParams.append(
          "startDate",
          startOfMonth.toISOString().split("T")[0]
        );
        queryParams.append("endDate", endOfMonth.toISOString().split("T")[0]);
      } else if (viewMode === "yearly") {
        const startOfYear = new Date(currentDate.getFullYear(), 0, 1);
        const endOfYear = new Date(currentDate.getFullYear(), 11, 31);
        queryParams.append(
          "startDate",
          startOfYear.toISOString().split("T")[0]
        );
        queryParams.append("endDate", endOfYear.toISOString().split("T")[0]);
      }

      // Add user data to query parameters
      if (userData && userData.id) {
        queryParams.append("userData", JSON.stringify(userData));
      }

      const url = `${process.env.REACT_APP_API_BASE_URL}/timesheet-test${queryParams.toString() ? "?" + queryParams.toString() : ""
        }`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.responseCode === 200) {
        setTimesheetEntries(data.data.entries || []);
      } else {
        setTimesheetEntries([]);
      }
    } catch (error) {
      // Fallback to mock data if API fails
      const mockEntries = [
        {
          id: "TS001",
          date: "2024-01-15",
          project: "CRM Development",
          task: "Frontend Development",
          description: "Working on user dashboard components",
          startTime: "09:00",
          endTime: "12:00",
          hours: 3,
          billable: true,
          status: "completed",
        },
        {
          id: "TS002",
          date: "2024-01-15",
          project: "CRM Development",
          task: "Bug Fixes",
          description: "Fixing authentication issues",
          startTime: "13:00",
          endTime: "16:30",
          hours: 3.5,
          billable: true,
          status: "completed",
        },
        {
          id: "TS003",
          date: "2024-01-15",
          project: "Internal Training",
          task: "React Workshop",
          description: "Attending React best practices workshop",
          startTime: "16:30",
          endTime: "18:00",
          hours: 1.5,
          billable: false,
          status: "completed",
        },
        {
          id: "TS004",
          date: "2024-01-14",
          project: "HRMS Development",
          task: "Backend API",
          description: "Developing employee management APIs",
          startTime: "09:00",
          endTime: "17:00",
          hours: 8,
          billable: true,
          status: "completed",
        },
      ];
      setTimesheetEntries(mockEntries);
    }
  };

  const projects = [
    "CRM Development",
    "HRMS Development",
    "Internal Training",
    "Client Support",
    "Test Project 1",
    "Updated Client Support",
  ];

  // Project ID mapping - in a real app, this would come from an API
  const projectIdMapping = {
    "CRM Development": "68e3c62e1a5dd48b3506929f",
    "HRMS Development": "68e3c62e1a5dd48b3506929f",
    "Internal Training": "68e3c62e1a5dd48b3506929f",
    "Client Support": "68e3c62e1a5dd48b3506929f",
    "Test Project 1": "68e3c62e1a5dd48b3506929f",
    "Updated Client Support": "68e3c62e1a5dd48b3506929f",
  };

  const tasks = {
    "CRM Development": [
      "Frontend Development",
      "Backend Development",
      "Testing",
      "Bug Fixes",
      "Documentation",
    ],
    "HRMS Development": [
      "Frontend Development",
      "Backend API",
      "Database Design",
      "Testing",
      "Integration",
    ],
    "Internal Training": [
      "React Workshop",
      "Node.js Training",
      "Database Training",
      "Security Training",
    ],
    "Client Support": [
      "Bug Investigation",
      "Feature Requests",
      "Technical Support",
      "Documentation",
    ],
    "Test Project 1": [
      "Test Task 1",
      "Test Task 2",
      "Testing",
      "Documentation",
    ],
    "Updated Client Support": [
      "Updated Documentation",
      "Bug Investigation",
      "Feature Requests",
      "Technical Support",
    ],
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const calculateHours = (startTime, endTime) => {
    if (!startTime || !endTime) return 0;
    const start = new Date(`2000-01-01T${startTime}:00`);
    const end = new Date(`2000-01-01T${endTime}:00`);
    const diff = (end - start) / (1000 * 60 * 60);
    return Math.max(0, diff);
  };

  // Handle key press for text inputs (alphanumeric, spaces, commas only)
  const handleTextKeyPress = (e) => {
    if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let processedValue = value;

    // Text fields: only allow alphanumeric, spaces, and commas
    if (name === 'description') {
      processedValue = value.replace(/[^a-zA-Z0-9\s,]/g, '');
      // Enforce maxLength
      if (processedValue.length > 500) processedValue = processedValue.slice(0, 500);
    }

    const newValue = type === "checkbox" ? checked : (processedValue || value);

    setFormData((prev) => {
      const updated = { ...prev, [name]: newValue };

      // Auto-calculate hours when start/end time changes
      if (name === "startTime" || name === "endTime") {
        const hours = calculateHours(
          name === "startTime" ? value : prev.startTime,
          name === "endTime" ? value : prev.endTime
        );
        updated.hours = hours.toFixed(1);
      }

      return updated;
    });
  };

  const handleSubmitEntry = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      // Get user data from localStorage
      const userData = JSON.parse(localStorage.getItem('user_data') || '{}');

      // Validate required fields
      if (
        !formData.project ||
        !formData.task ||
        !formData.startTime ||
        !formData.endTime
      ) {
        alert(
          "❌ Please fill in all required fields (Project, Task, Start Time, End Time)"
        );
        return;
      }

      if (!userData || !userData.id) {
        alert("❌ User data not found. Please login again.");
        return;
      }

      // Always create new entry (edit mode removed)
      // Transform form data to match backend expectations
      const requestData = {
        projectId: projectIdMapping[formData.project] || "68e3c62e1a5dd48b3506929f", // Dynamic project ID
        projectName: formData.project,
        task: formData.task,
        description: formData.description || "",
        date: currentDate.toISOString().split("T")[0],
        startTime: formData.startTime.includes(":")
          ? formData.startTime
          : formData.startTime + ":00", // Ensure proper time format
        endTime: formData.endTime.includes(":")
          ? formData.endTime
          : formData.endTime + ":00", // Ensure proper time format
        billable: formData.billable,
        category: "development",
        tags: [],
        comments: "",
        userData: userData, // Include user data
      };

      // Create new entry
      let response;
      try {
        response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/timesheet-test`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify(requestData),
          }
        );
      } catch (fetchError) {
        throw fetchError;
      }

      if (response.ok) {
        await response.json();
        alert("✅ Timesheet entry saved successfully!");
        resetForm();
        // Refresh the list
        await loadTimesheetEntries();
      } else {
        const errorText = await response.text();
        let errorMessage = "Unknown error";
        try {
          const errorResult = JSON.parse(errorText);
          errorMessage =
            errorResult.responseMessage || errorResult.message || errorMessage;
        } catch (e) {
          errorMessage = errorText;
        }
        alert("❌ Failed to save timesheet entry: " + errorMessage);
      }
    } catch (error) {
      alert("❌ Failed to save timesheet entry. Please try again.");
    }
  };

  const resetForm = () => {
    setFormData({
      project: "",
      task: "",
      description: "",
      startTime: "",
      endTime: "",
      hours: "",
      billable: true,
    });
    setShowAddEntry(false);
  };

  const handleDeleteEntry = async (entryId) => {
    try {
      // Get user data from localStorage
      const userData = JSON.parse(localStorage.getItem('user_data') || '{}');

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/timesheet-test/${entryId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ userData }), // Include user data
        }
      );

      if (response.ok) {
        await response.json();
        alert("✅ Timesheet entry deleted successfully!");
        // Refresh the list
        await loadTimesheetEntries();
      } else {
        const errorText = await response.text();
        let errorMessage = "Unknown error";
        try {
          const errorResult = JSON.parse(errorText);
          errorMessage =
            errorResult.message || errorResult.responseMessage || errorMessage;
        } catch (e) {
          errorMessage = errorText;
        }
        alert("❌ Failed to delete timesheet entry: " + errorMessage);
      }
    } catch (error) {
      alert("❌ Failed to delete timesheet entry. Please try again.");
    }
  };

  const startTimer = (project, task) => {
    setActiveTimer({ project, task, startTime: new Date() });
    setTimerSeconds(0);
  };

  const pauseTimer = () => {
    if (activeTimer) {
      // Save the current timer as an entry
      const hours = timerSeconds / 3600;
      const entry = {
        id: `TS${String(timesheetEntries.length + 1).padStart(3, "0")}`,
        date: currentDate.toISOString().split("T")[0],
        project: activeTimer.project,
        task: activeTimer.task,
        description: "Timer entry",
        startTime: activeTimer.startTime.toTimeString().slice(0, 5),
        endTime: new Date().toTimeString().slice(0, 5),
        hours: parseFloat(hours.toFixed(2)),
        billable: true,
        status: "completed",
      };
      setTimesheetEntries((prev) => [entry, ...prev]);
    }
    setActiveTimer(null);
    setTimerSeconds(0);
  };

  const stopTimer = () => {
    setActiveTimer(null);
    setTimerSeconds(0);
  };

  const getFilteredEntries = () => {
    // Since we're now fetching filtered data from the backend,
    // we can return the timesheetEntries directly
    // The backend handles project filtering via the 'search' parameter
    // and date filtering via startDate/endDate parameters
    return timesheetEntries;
  };

  const getTotalHours = () => {
    const filtered = getFilteredEntries();
    return filtered.reduce((total, entry) => total + entry.hours, 0);
  };

  const getBillableHours = () => {
    const filtered = getFilteredEntries();
    return filtered
      .filter((entry) => entry.billable)
      .reduce((total, entry) => total + entry.hours, 0);
  };

  const navigateDate = (direction) => {
    const newDate = new Date(currentDate);
    if (viewMode === "daily") {
      newDate.setDate(currentDate.getDate() + direction);
    } else if (viewMode === "weekly") {
      newDate.setDate(currentDate.getDate() + direction * 7);
    } else if (viewMode === "monthly") {
      newDate.setMonth(currentDate.getMonth() + direction);
    } else if (viewMode === "yearly") {
      newDate.setFullYear(currentDate.getFullYear() + direction);
    }
    setCurrentDate(newDate);
  };

  const getDateRangeText = () => {
    if (viewMode === "daily") {
      return currentDate.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } else if (viewMode === "weekly") {
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      return `${startOfWeek.toLocaleDateString()} - ${endOfWeek.toLocaleDateString()}`;
    } else if (viewMode === "monthly") {
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      return `${startOfMonth.toLocaleDateString()} - ${endOfMonth.toLocaleDateString()}`;
    } else if (viewMode === "yearly") {
      const startOfYear = new Date(currentDate.getFullYear(), 0, 1);
      const endOfYear = new Date(currentDate.getFullYear(), 11, 31);
      return `${startOfYear.toLocaleDateString()} - ${endOfYear.toLocaleDateString()}`;
    } else {
      return currentDate.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
      });
    }
  };

  const handleExport = () => {
    try {
      const filteredEntries = getFilteredEntries();

      if (filteredEntries.length === 0) {
        alert("No timesheet entries to export");
        return;
      }

      // Get user data for export header
      const userData = JSON.parse(localStorage.getItem('user_data') || '{}');

      // Prepare CSV data with comprehensive headers
      const csvData = [
        [
          "Employee ID",
          "Employee Name",
          "Employee Email",
          "Date",
          "Project ID",
          "Project Name",
          "Project Code",
          "Client Name",
          "Task",
          "Description",
          "Start Time",
          "End Time",
          "Duration",
          "Hours",
          "Billable",
          "Billable Rate",
          "Total Amount",
          "Status",
          "Category",
          "Location",
          "Comments",
          "Created At",
          "Updated At"
        ],
      ];

      filteredEntries.forEach((entry) => {
        csvData.push([
          entry.employeeId || "",
          entry.employeeName || "",
          entry.employeeEmail || "",
          entry.date || "",
          entry.projectId || "",
          entry.projectName || "",
          entry.projectCode || "",
          entry.clientName || "",
          entry.task || "",
          entry.description || "",
          entry.startTime || "",
          entry.endTime || "",
          entry.duration || "",
          entry.hours ? entry.hours.toString() : "0",
          entry.billable ? "Yes" : "No",
          entry.billableRate ? entry.billableRate.toString() : "0",
          entry.totalAmount ? entry.totalAmount.toString() : "0",
          entry.status || "draft",
          entry.category || "",
          entry.location ? (typeof entry.location === 'object' ? entry.location.type || "" : entry.location) : "",
          entry.comments || "",
          entry.createdAt ? new Date(entry.createdAt).toLocaleString() : "",
          entry.updatedAt ? new Date(entry.updatedAt).toLocaleString() : ""
        ]);
      });



      // Convert array to CSV string
      const csvContent = csvData
        .map((row) => row.map((field) => `"${field}"`).join(","))
        .join("\n");

      // Create and download CSV file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);

      // Create descriptive filename
      const userName = userData.firstName ? `${userData.firstName}_${userData.lastName}` : "User";
      const dateStr = new Date().toISOString().split("T")[0];
      const periodStr = viewMode === "daily" ? "daily" : viewMode === "weekly" ? "weekly" : "monthly";

      link.setAttribute(
        "download",
        `timesheet_${userName}_${periodStr}_${dateStr}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Show success notification
      const notification = document.createElement("div");
      notification.className =
        "fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50";
      notification.textContent = `Timesheet exported successfully! ${filteredEntries.length} entries exported.`;
      document.body.appendChild(notification);

      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 4000);
    } catch (error) {
      alert("Failed to export timesheet. Please try again.");
    }
  };

  if (showAddEntry) {
    return (
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={resetForm}
              className="p-2"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h2 className="text-xl font-semibold text-gray-900">
              Add Time Entry
            </h2>
          </div>
        </div>

        <form onSubmit={handleSubmitEntry} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project *
              </label>
              <select
                name="project"
                value={formData.project}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F2942]/50 focus:border-transparent"
              >
                <option value="">Select project</option>
                {projects.map((project) => (
                  <option key={project} value={project}>
                    {project}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Task *
              </label>
              <select
                name="task"
                value={formData.task}
                onChange={handleInputChange}
                required
                disabled={!formData.project}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F2942]/50 focus:border-transparent disabled:bg-gray-100"
              >
                <option value="">Select task</option>
                {formData.project &&
                  tasks[formData.project]?.map((task) => (
                    <option key={task} value={task}>
                      {task}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Time *
              </label>
              <input
                type="time"
                name="startTime"
                value={formData.startTime}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F2942]/50 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Time *
              </label>
              <input
                type="time"
                name="endTime"
                value={formData.endTime}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F2942]/50 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hours
              </label>
              <input
                type="number"
                name="hours"
                value={formData.hours}
                onChange={handleInputChange}
                step="0.1"
                min="0"
                max="24"
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-[#0F2942]/50 focus:border-transparent"
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                name="billable"
                checked={formData.billable}
                onChange={handleInputChange}
                className="h-4 w-4 text-[#0F2942] focus:ring-[#0F2942]/50 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-700">
                Billable
              </label>
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              onKeyPress={handleTextKeyPress}
              required
              rows={3}
              maxLength={1500}
              placeholder="Describe the work performed..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F2942]/50 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              {formData.description.length}/1500 characters
            </p>
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <Button type="button" variant="secondary" onClick={resetForm}>
              Cancel entry
            </Button>
            <PermissionGate permission="attendance" action={showAddEntry ? "create" : "update"}>
              <Button
                type="submit"
                variant="primary"
                icon={
                  <Save className="h-4 w-4" />
                }
                aria-label="Save time entry"
              >
                <span>Save time entry</span>
              </Button>
            </PermissionGate>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Button variant="ghost" size="sm" onClick={onBack} className="p-2">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h2 className="text-xl font-semibold text-gray-900">Timesheet</h2>
          </div>
          <div className="flex items-center space-x-3">
            <PermissionGate permission="attendance" action="read">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleExport}
                className="flex items-center space-x-2"
              >
                <Download className="h-4 w-4" />
                <span>Export</span>
              </Button>
            </PermissionGate>
            <PermissionGate permission="attendance" action="create">
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  setShowAddEntry(true);
                }}
                className="flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>Add time entry</span>
              </Button>
            </PermissionGate>
          </div>
        </div>
      </div>

      {/* Timer Section */}
      {activeTimer && (
        <div className="p-4 bg-blue-50 border-b border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-gray-700">
                  Timer Running
                </span>
              </div>
              <div className="text-lg font-mono font-bold text-[#0F2942]">
                {formatTime(timerSeconds)}
              </div>
              <div className="text-sm text-gray-600">
                {activeTimer.project} - {activeTimer.task}
              </div>
            </div>
            <div className="flex space-x-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={pauseTimer}
                className="flex items-center space-x-1"
              >
                <Pause className="h-4 w-4" />
                <span>Pause and save timer</span>
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={stopTimer}
                className="flex items-center space-x-1"
              >
                <Square className="h-4 w-4" />
                <span>Stop</span>
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          {/* Date Navigation */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigateDate(-1)}
                className="p-2"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="text-lg font-medium text-gray-900 min-w-0">
                {getDateRangeText()}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigateDate(1)}
                className="p-2"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setCurrentDate(new Date())}
            >
              Today
            </Button>
          </div>

          {/* View Mode and Filters */}
          <div className="flex items-center space-x-4">
            <select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F2942]/50 focus:border-transparent"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F2942]/50 focus:border-transparent"
            >
              <option value="all">All Projects</option>
              {projects.map((project) => (
                <option key={project} value={project}>
                  {project}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="p-6 border-b border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Clock className="h-5 w-5 text-[#0F2942]" />
              <span className="text-sm font-medium text-blue-900">
                Total Hours
              </span>
            </div>
            <div className="text-2xl font-bold text-[#0F2942] mt-1">
              {getTotalHours().toFixed(1)}h
            </div>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <BarChart3 className="h-5 w-5 text-green-600" />
              <span className="text-sm font-medium text-green-900">
                Billable Hours
              </span>
            </div>
            <div className="text-2xl font-bold text-green-600 mt-1">
              {getBillableHours().toFixed(1)}h
            </div>
          </div>
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Calendar className="h-5 w-5 text-purple-600" />
              <span className="text-sm font-medium text-purple-900">
                Entries
              </span>
            </div>
            <div className="text-2xl font-bold text-purple-600 mt-1">
              {getFilteredEntries().length}
            </div>
          </div>
        </div>
      </div>

      {/* Timesheet Entries */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Project
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Task
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Time
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Hours
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Billable
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {getFilteredEntries().map((entry) => (
              <tr key={entry.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {new Date(entry.date).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {entry.project}
                  </div>
                  <div className="text-sm text-gray-500 truncate max-w-xs">
                    {entry.projectName}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {entry.task}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {entry.startTime} - {entry.endTime}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {entry.hours}h
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${entry.billable
                      ? "bg-green-100 text-green-800"
                      : "bg-gray-100 text-gray-800"
                      }`}
                  >
                    {entry.billable ? "Billable" : "Non-billable"}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <PermissionGate permission="attendance" action="delete">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteEntry(entry.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </PermissionGate>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {getFilteredEntries().length === 0 && (
        <div className="text-center py-12">
          <Clock className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            No time entries found
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Start tracking your time by adding a new entry.
          </p>
        </div>
      )}
    </div>
  );
};

export default Timesheet;
